/**
 * Per-case snapshot of the Analysis (Phase 2) wizard's in-progress state.
 *
 * The wizard's Zustand store (`analysis-storage`) uses a single GLOBAL persist key,
 * so it only holds ONE case at a time — opening a different case resets it and wipes
 * the prior case's systems/canvases/mappings. That's what makes the deep-landing
 * guard bounce a resumed draft back to Step 0 (the earlier steps' data is gone).
 *
 * This module keeps a small per-case archive alongside the global store: on a case
 * switch the wizard archives the OUTGOING case's slice under its own key and restores
 * the INCOMING case's slice if one exists, so revisiting a case keeps its data and a
 * saved draft lands on the right step. Backend rehydration would be the fully durable
 * fix; this is the low-risk, same-browser cache.
 *
 * All operations fail silently when storage is unavailable (private mode / quota).
 */

/**
 * The archive key must include the case's creation time, not just its id.
 *
 * Case ids are REUSED: the backend mints "proj_001" again after the database is
 * cleared, so a new case can carry a previous case's id. Keying on the id alone
 * meant `readAnalysisArchive("proj_001")` restored the OLD case's canvases into
 * the new one — the same bleed the store's scope check was meant to prevent, via
 * the archive instead. A consumer-goods case restored an oil-refinery schema
 * this way.
 *
 * `createdAt` is omitted only for state written by an older build; those entries
 * keep the legacy id-only key and are still readable, but a case whose createdAt
 * is known will never match one.
 */
const KEY = (caseId: string, createdAt?: string | null) =>
  createdAt
    ? `analysis-archive:${caseId}:${createdAt}`
    : `analysis-archive:${caseId}`;

export function writeAnalysisArchive(
  caseId: string | null | undefined,
  slice: Record<string, unknown>,
  createdAt?: string | null
): void {
  if (!caseId) return;
  try {
    localStorage.setItem(KEY(caseId, createdAt), JSON.stringify(slice));
  } catch {
    /* storage disabled or full — non-fatal */
  }
}

export function readAnalysisArchive(
  caseId: string | null | undefined,
  createdAt?: string | null
): Record<string, unknown> | null {
  if (!caseId) return null;
  try {
    const raw = localStorage.getItem(KEY(caseId, createdAt));
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function clearAnalysisArchive(
  caseId: string | null | undefined,
  createdAt?: string | null
): void {
  if (!caseId) return;
  try {
    localStorage.removeItem(KEY(caseId, createdAt));
  } catch {
    /* non-fatal */
  }
}
